/// <amd-module name="VSSUI/Utilities/Focus" />
import { IFocusGroupContext } from "VSSUI/FocusGroup";
import { noop } from "VSSUI/Util";
import { getWindow } from "./Dom";

export interface IFocusable<T> {
    focus(options?: T): void;
}

/**
 * IFocusableProps are used to describe how the element is focused. Whether or not
 * you can tab to the element. Whether or not clicking on the element or calling
 * its focus method will set focus.
 *
 * Elements MUST either have a tabIndex set, or be native element that supports
 * focus to recieve keyboard focus.
 */
export interface IFocusableProps {
    /**
     * Disabled elements are not tabable by default.
     */
    disabled?: boolean;

    /**
     * The element has been exluded from a current focusZone by default.
     */
    excludeFocusZone?: boolean;

    /**
     * The element should not be tabable but still should be able to receieve focus.
     */
    excludeTabStop?: boolean;

    /**
     * If an element participates in a FocusGroup, it MUST have a unique identifier
     * that the group uses to track the active focus element.
     */
    id?: string;

    /**
     * An explicit tabIndex that should be used on the element.
     */
    tabIndex?: number;
}

/**
 * getTabIndex takes in a standard set of focus related properties to determine
 * the correct tabIndex for the element.
 */
export function getTabIndex(props: IFocusableProps, focusGroupContext?: IFocusGroupContext): number | undefined {
    // Return an explicit tabIndex if one was requested.
    if (props.tabIndex !== undefined) {
        return props.tabIndex;
    }
    // Make element tabbable if:
    //  The element is not disabled,
    //  The element does not have the excludeTabStop property
    //  Optionally, the component is within a focusGroup and is the focused element
    if (
        !props.disabled &&
        !props.excludeTabStop &&
        (!focusGroupContext || focusGroupContext.onFocus === noop || focusGroupContext.focusedElementId === props.id)
    ) {
        return 0;
    }

    // Allow the element to have focus as long as the excludeFocusZone was not supplied.
    if (!props.excludeFocusZone) {
        return -1;
    }

    // The element is not tabbable and wont have a tabIndex, therefore it can't get focus.
    return undefined;
}

let targetToFocusOnNextRepaint: HTMLElement | IFocusable<any> | { focus(): void } | undefined = undefined;

/**
 * Sets focus on the next frame OR after setTimeout(0)
 * If you pass an IFocusable instead of an HTMLElement it'll use setTimeout instead of window.requestAnimationFrame
 */
export function focusAsync(element: HTMLElement | IFocusable<any> | { focus(): void }): void {
    // If we've called this method this frame, re-set the element to focus but don't hook up another handler
    if (targetToFocusOnNextRepaint) {
        targetToFocusOnNextRepaint = element;
        return;
    }

    // If this is the first time we've called this this frame, set an event handler
    // or schedule it async if we can't get the window element
    targetToFocusOnNextRepaint = element;
    const window = getWindow(element as HTMLElement);

    if (window) {
        window.requestAnimationFrame(() => {
            if (targetToFocusOnNextRepaint) {
                targetToFocusOnNextRepaint.focus();
            }
            targetToFocusOnNextRepaint = undefined;
        });
    } else {
        setTimeout(() => {
            if (targetToFocusOnNextRepaint) {
                targetToFocusOnNextRepaint.focus();
            }
        });
    }
}
